<template>
  <v-card
    class="competition-card"
    elevation="0"
    @click="$emit('click', competition)"
  >
    <div class="card-content">
      <!-- Club Info Badge -->
      <div v-if="competition.clubName" class="club-badge mb-3">
        <v-avatar v-if="clubLogo" size="20" class="mr-2">
          <v-img :src="clubLogo" :alt="competition.clubName" />
        </v-avatar>
        <v-icon v-else size="14" class="mr-1">fa-regular fa-users</v-icon>
        <span class="text-caption">{{ competition.clubName }}</span>
      </div>

      <!-- Competition Name -->
      <div class="text-h2 font-weight-bold mb-4 competition-title">{{ competition.name }}</div>

      <!-- Competition Info Row -->
      <div class="competition-info">
        <div class="info-item">
          <v-icon size="14">fa-regular fa-calendar</v-icon>
          <span class="text-body-2">{{ formattedStartDate }}</span>
        </div>
        <div class="info-item">
          <v-icon size="14">fa-regular fa-user</v-icon>
          <span class="text-body-2">{{ competition.participants }} participants</span>
        </div>
        <div class="info-item">
          <v-icon size="14">fa-regular fa-trophy</v-icon>
          <span class="text-body-2">{{ competition.prizes }} prizes</span>
        </div>
      </div>

      <!-- Countdown Timer - 5 Boxes Layout -->
      <div class="countdown-timer">
        <div :class="['timer-box', 'status-box', statusColorClass]">
          <span class="status-text">{{ statusText }}</span>
        </div>
        <div class="timer-box">
          <div class="text-h5 font-weight-bold">{{ padNumber(countdown.days) }}</div>
          <div class="text-caption">Day</div>
        </div>
        <div class="timer-box">
          <div class="text-h5 font-weight-bold">{{ padNumber(countdown.hours) }}</div>
          <div class="text-caption">Hou</div>
        </div>
        <div class="timer-box">
          <div class="text-h5 font-weight-bold">{{ padNumber(countdown.minutes) }}</div>
          <div class="text-caption">Min</div>
        </div>
        <div class="timer-box">
          <div class="text-h5 font-weight-bold">{{ padNumber(countdown.seconds) }}</div>
          <div class="text-caption">Sec</div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import competitionService from '../services/competition.service'
import realCompetitionService from '../services/real-competition.service'

export default {
  name: 'CompetitionCard',
  props: {
    competition: {
      type: Object,
      required: true
    },
    mode: {
      type: String,
      default: 'live'
    }
  },
  data() {
    return {
      countdown: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        status: this.competition.status
      },
      countdownInterval: null,
      clubLogo: null
    }
  },
  computed: {
    formattedStartDate() {
      // Parse the date and force UTC interpretation
      const [year, month, day] = this.competition.startDate.split('T')[0].split('-').map(Number)
      const date = new Date(Date.UTC(year, month - 1, day))
      
      console.log('Formatting date:', {
        originalDate: this.competition.startDate,
        parsedDate: date.toISOString(),
        utcComponents: {
          year, month, day
        }
      })
      
      return new Intl.DateTimeFormat('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        timeZone: 'UTC'  // Force UTC interpretation
      }).format(date)
    },
    statusText() {
      switch(this.competition.status) {
        case 'begins_in':
          return 'BEGINS IN:'
        case 'ends_in':
          return 'ENDS IN:'
        case 'ended':
          return 'ENDED'
        default:
          return ''
      }
    },
    statusColorClass() {
      switch(this.competition.status) {
        case 'begins_in':
          return 'status-begins'
        case 'ends_in':
          return 'status-ends'
        case 'ended':
          return 'status-ended'
        default:
          return ''
      }
    }
  },
  async mounted() {
    console.log('Mounted CompetitionCard for competition:', this.competition);
    this.startCountdown()

    // Load club logo if clubId is available
    if (this.competition.clubId) {
      this.clubLogo = await realCompetitionService.getClubLogo(this.competition.clubId)
    }
  },
  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  },
  methods: {
    startCountdown() {
      this.updateCountdown()
      this.countdownInterval = setInterval(() => {
        this.updateCountdown()
      }, 1000)
    },
    updateCountdown() {
      // Parse the target date and force UTC interpretation
      const targetDateStr = this.competition.status === 'begins_in'
        ? this.competition.startDate
        : this.competition.endDate

      const { days, hours, minutes, seconds } = competitionService.calculateCountdown(targetDateStr)

      this.countdown = {
        days,
        hours,
        minutes,
        seconds,
        status: this.competition.status
      }

      // Check if countdown reached zero
      if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval)
        }
      }
    },
    padNumber(num) {
      return num.toString().padStart(2, '0')
    }
  }
}
</script>

<style lang="scss" scoped>
.competition-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: none !important;
  border-radius: 12px;
  background: white !important;
  box-shadow: 0px 5px 10px 0px rgba(44, 44, 44, 0.1) !important;
  max-width: 400px;
  width: 100%;

  // Mobile responsive - full width
  @media (max-width: 600px) {
    max-width: 100%;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0px 8px 16px 0px rgba(44, 44, 44, 0.15) !important;
  }

  .card-content {
    padding: 20px;

    // Mobile padding adjustment
    @media (max-width: 600px) {
      padding: 16px;
    }

    .club-badge {
      display: flex;
      align-items: center;
      background: #F5F5F5;
      border-radius: 16px;
      padding: 4px 12px;
      width: fit-content;
      color: #666666;

      .v-icon {
        color: #666666;
      }

      .text-caption {
        color: #666666;
        font-weight: 500;
      }
    }

    .competition-title {
      color: #000000 !important;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1.5 !important;
      height: auto;
      min-height: 0;
      padding-bottom: 2px;
    }

    .competition-info {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      margin-bottom: 40px;

      .info-item {
        display: flex;
        align-items: center;
        gap: 6px;

        .v-icon {
          color: black;
        }
      }
    }

    .countdown-timer {
      display: flex;
      gap: 8px;

      .timer-box {
        flex: 1;
        background: #EEEEEE;
        border-radius: 8px;
        padding: 12px 8px;
        text-align: center;
        min-height: auto;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 4px;

        &.status-box {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px 4px;
          min-height: auto;

          .status-text {
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.5px;
            line-height: 1.3;
            word-break: break-word;
          }

          &.status-begins {
            background: #FFF75F;
            color: #000000;
          }

          &.status-ends {
            background: #00C361;
            color: #FFFFFF;
          }

          &.status-ended {
            background: #9E9E9E;
            color: #FFFFFF;
          }
        }
      }
    }
  }
}
</style>